import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, FileText, User, ExternalLink } from 'lucide-react';
import {
  AdminClientApi,
  GetThoughtResponseInterface,
  ClientThoughtWithAuthor,
} from '../../Admin-ClientLibrary';
import BeliefsListView from '../../components/BeliefsListView';
import { getApiUrl } from '../../config/api';
import { useRecentEntities } from '../../contexts/RecentEntitiesContext';
import './ThoughtDetail.css';

// Initialize the Admin Client Library
const getToken = (): string => {
  return localStorage.getItem('authToken') || '';
};

const adminApi = new AdminClientApi(getApiUrl(), getToken);

const ThoughtDetail: React.FC = () => {
  const { thoughtId } = useParams<{ thoughtId: string }>();
  const navigate = useNavigate();
  const [thought, setThought] = useState<ClientThoughtWithAuthor | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const { addRecentEntity } = useRecentEntities();

  useEffect(() => {
    if (thoughtId) {
      const thoughtIdNum = parseInt(thoughtId, 10);

      // Validate that the parsed number is valid
      if (isNaN(thoughtIdNum) || thoughtIdNum <= 0) {
        setError('Invalid thought ID in URL');
        setLoading(false);
        return;
      }

      fetchThought(thoughtIdNum);
    }
  }, [thoughtId]);

  const fetchThought = async (thoughtIdNum: number): Promise<void> => {
    try {
      setLoading(true);
      setError(null);

      // Fetch thought data using getThought API
      const thoughtResponse = await adminApi.getThought({ thoughtId: thoughtIdNum });

      if (thoughtResponse.success && thoughtResponse.thought) {
        setThought(thoughtResponse.thought);
        addRecentEntity('thought', thoughtResponse.thought);
      } else {
        setError(thoughtResponse.message || 'Thought not found');
        return;
      }
    } catch (err: any) {
      console.error('Error fetching thought:', err);
      setError(err.message || 'Failed to fetch thought data');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date: Date): string => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const handleViewBeliefs = (): void => {
    if (thought) {
      navigate(`/beliefs?thoughtId=${thought.content_id}`);
    }
  };

  if (loading) {
    return (
      <div className="thought-detail-page">
        <div className="loading">Loading thought details...</div>
      </div>
    );
  }

  if (error || !thought) {
    return (
      <div className="thought-detail-page">
        <div className="error-message">
          <p>Error: {error || 'Failed to load thought data'}</p>
          <button onClick={() => navigate('/thoughts')} className="btn-secondary">
            Back to Thoughts
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="thought-detail-page">
      <div className="thought-detail-header">
        <button onClick={() => navigate('/thoughts')} className="back-button">
          <ArrowLeft size={16} />
          Back to Thoughts
        </button>

        <div className="thought-detail-title">
          <div className="thought-icon">
            <FileText size={24} />
          </div>
          <div className="thought-title-info">
            <h1>Thought Details</h1>
            <div className="thought-meta">
              <span className="thought-id">Thought ID: {thought.content_id}</span>
              <span className="thought-created">Created: {formatDate(thought.createdAt)}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="thought-detail-content">
        {/* Thought Content Card */}
        <div className="info-card">
          <div className="info-card-header">
            <FileText size={20} />
            <h3>Thought Content</h3>
          </div>
          <div className="info-card-content">
            <div className="thought-text-display">
              <p>{thought.text}</p>
            </div>
            <div className="thought-metadata">
              <div className="metadata-grid">
                <div className="metadata-item">
                  <strong>Content ID:</strong> {thought.content_id}
                </div>
                <div className="metadata-item">
                  <strong>Created:</strong> {formatDate(thought.createdAt)}
                </div>
                <div className="metadata-item">
                  <strong>Last Updated:</strong> {formatDate(thought.updatedAt)}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Beliefs on this Thought Section */}
        <div className="user-beliefs-section">
          <div className="user-beliefs-header">
            <h3>Beliefs on this Thought</h3>
            <button className="btn-secondary view-all-btn" onClick={handleViewBeliefs}>
              <ExternalLink size={14} />
              View All Beliefs
            </button>
          </div>
          <BeliefsListView
            filters={{ thoughtId: thought.content_id }}
            showCheckboxes={false}
            showBulkActions={false}
            compact={false}
            limit={8}
          />
        </div>
      </div>
    </div>
  );
};

export default ThoughtDetail;
